/**
 * Print-failure error registry — single source of truth.
 *
 * Every way a print can fail (backend resolution → agent download → local open
 * → connectivity) maps to ONE stable `PrintErrorCode`. This table is the single
 * place that defines, for each code:
 *   - which pipeline `stage` it belongs to (dev triage / Sentry-Railway filter),
 *   - the backend HTTP status it maps to (resolve-stage codes),
 *   - whether re-firing the same print is worth attempting (`retryable`),
 *   - the operator-facing `severity` (drives amber-vs-red in the UI),
 *   - the i18n key base the frontend resolves to operator copy,
 *   - a one-line `devMeaning` (also rendered into the reference doc).
 *
 * Imported by ALL three layers so they cannot drift:
 *   - backend  (apps/backend/src/routers/print-agent.ts) — emits `{ error: code }`,
 *   - agent    (apps/print-agent/src/index.ts)           — classifies + forwards,
 *   - frontend (features/print-station)                  — maps code → copy.
 *
 * The agent is bundled by esbuild and DOES resolve `@threadheads/shared`
 * (see apps/print-agent/src/sku-parser.ts), so there is no hand-mirrored copy.
 *
 * Reference doc generated from this table:
 *   .planning/reference/print-error-codes.md
 */
export const PRINT_ERROR_META = {
    // ── resolve ────────────────────────────────────────────────────────────
    invalid_sku: {
        stage: 'resolve',
        httpStatus: 400,
        retryable: false,
        severity: 'terminal',
        i18nKey: 'printer.errInvalidSku',
        devMeaning: 'SKU empty or >200 chars, rejected before any DB lookup.',
    },
    invalid_location_id: {
        stage: 'resolve',
        httpStatus: 400,
        retryable: false,
        severity: 'config',
        i18nKey: 'printer.errInvalidLocation',
        devMeaning: 'locationId is not a UUID. The print station is sending a bad location id.',
    },
    rate_limited: {
        stage: 'resolve',
        httpStatus: 429,
        retryable: true,
        severity: 'retry',
        i18nKey: 'printer.errRateLimited',
        devMeaning: 'Per-IP rate limiter on the backend print-agent routes tripped.',
    },
    service_unavailable: {
        stage: 'resolve',
        httpStatus: 503,
        retryable: true,
        severity: 'retry',
        i18nKey: 'printer.errServiceUnavailable',
        devMeaning: 'Backend DB/Supabase unavailable, or a transient location-owner lookup error.',
    },
    print_file_not_available: {
        stage: 'resolve',
        httpStatus: 404,
        retryable: false,
        severity: 'terminal',
        i18nKey: 'printer.errNoPrintFile',
        devMeaning: "SKU's owning org couldn't be resolved OR no print-file row is mapped. Deliberately " +
            'merged into one code so the response never leaks whether the SKU exists in another org.',
    },
    location_not_configured: {
        stage: 'resolve',
        httpStatus: 404,
        retryable: false,
        severity: 'config',
        i18nKey: 'printer.errLocationNotConfigured',
        devMeaning: 'No locations row for the supplied locationId (post-auth, safe to distinguish).',
    },
    forbidden: {
        stage: 'resolve',
        httpStatus: 403,
        retryable: false,
        severity: 'config',
        i18nKey: 'printer.errForbidden',
        devMeaning: "Class-A gate or storage-config denied this org printing at this location.",
    },
    credentials_not_configured: {
        stage: 'resolve',
        httpStatus: 412,
        retryable: false,
        severity: 'config',
        i18nKey: 'printer.errCredentials',
        devMeaning: 'GCS/Tencent storage credentials are not configured for this location.',
    },
    custom_artwork_absent: {
        stage: 'resolve',
        httpStatus: 404,
        retryable: false,
        severity: 'terminal',
        i18nKey: 'printer.errCustomArtworkAbsent',
        devMeaning: 'Custom artwork file not found in storage (or an un-parseable custom barcode). ' +
            'Post-auth, so safe to distinguish from the generic no-print-file case.',
    },
    invalid_filename: {
        stage: 'resolve',
        httpStatus: 500,
        retryable: false,
        severity: 'config',
        i18nKey: 'printer.errInvalidFilename',
        devMeaning: 'Lookup/resolver produced a filename failing the safety check. Integrity issue, escalate.',
    },
    // ── download ───────────────────────────────────────────────────────────
    artwork_file_absent: {
        stage: 'download',
        retryable: false,
        severity: 'terminal',
        i18nKey: 'printer.errArtworkFileAbsent',
        devMeaning: 'Presigned object returned 404/NoSuchKey. The file is not in the bucket at download time.',
    },
    storage_forbidden: {
        stage: 'download',
        retryable: false,
        severity: 'config',
        i18nKey: 'printer.errStorageForbidden',
        devMeaning: 'Presigned fetch 403 (AccessDenied / SignatureDoesNotMatch). IAM, clock skew, or expired signature.',
    },
    storage_unreachable: {
        stage: 'download',
        retryable: true,
        severity: 'retry',
        i18nKey: 'printer.errStorageUnreachable',
        devMeaning: 'Storage 5xx/timeout/DNS/TLS during download, OR the backend failed to mint the presigned URL (presign throttle/infra).',
    },
    // ── open ───────────────────────────────────────────────────────────────
    print_open_failed: {
        stage: 'open',
        retryable: true,
        severity: 'retry',
        i18nKey: 'printer.printFailedOpen',
        devMeaning: 'File cached fine but the local handler (RIP / Invoke-Item) exited non-zero. Stderr in the beacon.',
    },
    print_file_consumed: {
        stage: 'open',
        retryable: false,
        severity: 'info',
        i18nKey: 'printer.errFileConsumed',
        devMeaning: 'Cached file vanished before open. A RIP folder-watcher consumed it. The file already went to the RIP; auto-open should be OFF on this machine.',
    },
    disk_full: {
        stage: 'open',
        // Worth re-firing — but only AFTER the operator frees disk space. The copy
        // tells them so; severity is 'config' (red) because re-tapping alone won't help.
        retryable: true,
        severity: 'config',
        i18nKey: 'printer.errDiskFull',
        devMeaning: 'Download succeeded but writing the file to the local cache failed with ENOSPC. The operator machine hard drive is full.',
    },
    // ── connectivity ───────────────────────────────────────────────────────
    agent_unreachable: {
        stage: 'connectivity',
        retryable: true,
        severity: 'retry',
        i18nKey: 'printer.errAgentUnreachable',
        devMeaning: 'No agent answered on any candidate port. Detected by the frontend, not the sidecar.',
    },
    // ── fallback ───────────────────────────────────────────────────────────
    unknown: {
        stage: 'resolve',
        retryable: true,
        severity: 'retry',
        i18nKey: 'printer.printFailedRetry',
        devMeaning: 'Unclassified failure. See the canonical message / beacon body snippet.',
    },
};
/** All codes, handy for tests and the doc generator. */
export const PRINT_ERROR_CODES = Object.keys(PRINT_ERROR_META);
/** Narrowing helper — true when `value` is a known PrintErrorCode. */
export function isPrintErrorCode(value) {
    return typeof value === 'string' && value in PRINT_ERROR_META;
}
/**
 * Resolve any value (a backend `error` string, a thrown reason, etc.) to a
 * known code, falling back to `unknown`. Keeps callers from special-casing the
 * fallback everywhere.
 */
export function toPrintErrorCode(value) {
    return isPrintErrorCode(value) ? value : 'unknown';
}
